import React, { useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { updateJobOrder } from '../services/job-order-service';

const NAVY      = '#2d3748';
const SLATE     = '#4a5568';
const ACCENT    = '#5b7fa4';
const LIGHT_BG  = '#f0f4f8';

const NAVY_RGB  = [45, 55, 72];
const DARK      = [40, 40, 40];
const WHITE     = [255, 255, 255];
const LIGHT     = [240, 240, 240];
const HDR_BG    = [235, 240, 248];

const PAGE_W = 279.4;
const PAGE_H = 215.9;
const MARGIN = 10;
const HDR_H  = 38;
const FTR_H  = 10;

const LOGO_URL = '/brand/sdmillwork-logo.png';
let _logo = null;
async function getLogo() {
  if (_logo) return _logo;
  try {
    const res = await fetch(LOGO_URL);
    const blob = await res.blob();
    return new Promise(r => {
      const fr = new FileReader();
      fr.onloadend = () => { _logo = fr.result; r(_logo); };
      fr.onerror = () => r(null);
      fr.readAsDataURL(blob);
    });
  } catch { return null; }
}

const TWP_TYPE_LABELS = {
  DH: 'Double Hung', HS: 'Horizontal Slider', CSMT: 'Casement',
  PW: 'Picture Window', BSMT: 'Basement Hopper', '3HS': '3-Lite Slider', BM: 'Brickmould',
};

function getTypeLabel(w) {
  if (w.isMulledUnit) return w.mulledUnitLabel || 'Mulled/Joined Unit';
  if (w.twpType && TWP_TYPE_LABELS[w.twpType]) {
    let label = TWP_TYPE_LABELS[w.twpType];
    if (w.twpConfig && w.twpConfig !== 'Single') label += ' (' + w.twpConfig + ')';
    return label;
  }
  return w.style || w.description || w.type || 'Window/Door';
}

function getDimensions(w) {
  if (w.width && w.height) return w.width + '" \u00d7 ' + w.height + '"';
  return '';
}

function getLineSuffix(w) {
  const lt = (w.lineType || '').toLowerCase();
  const desc = (w.style || w.description || '').toLowerCase();
  if (lt === 'door' || ['door','patio door','storm door','exterior door','sliding door','side lite'].some(d => desc.includes(d))) return 'D';
  if (lt === 'ordered_window') return 'OW';
  return '';
}

const ADDER_TYPES = [
  'inside trim','outside trim','brick mold','coving','rework opening',
  'remove whole frame','remove rotten seals','trash removal','screen repair',
  'glass repair','shipping','delivery','mileage','setup','miscellaneous',
  'misc','service call','permit','tax','trim out','trim','window installation',
  'brickmold','shipping / delivery','setup / misc. fee',
];

function isWindowOrDoor(w) {
  if (w.lineType === 'adder' || w.type === 'adder') return false;
  const desc = (w.style || w.description || '').toLowerCase();
  if (ADDER_TYPES.some(a => desc.includes(a.toLowerCase()))) return false;
  if (w.lineType === 'note' || w.type === 'note') return false;
  return true;
}

function fmtDate() {
  return new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Group mulled panes into a single trim entry ───────────────────────────────
// For trim purposes, a mulled unit is ONE item with the TOTAL width × height.
// Individual panes (R1C1, R1C2, etc.) are collapsed into their parent unit.
// Returns an array of "trim items" — either a single window/door OR a grouped mulled unit.
// Build a lookup map: lineIndex -> estimate line.
// Used to back-fill mulled metadata for legacy job orders that lost it on save.
function buildEstimateLineMap(estimate) {
  const map = new Map();
  if (!estimate?.lines) return map;
  estimate.lines.forEach((line, i) => {
    if (line && line.type !== 'note') map.set(i, line);
  });
  return map;
}

// Look up the parent mulled line for a window and return back-fill info,
// or null if the window isn't from a mulled estimate line.
function getMulledMetadataFromEstimate(w, estimate) {
  if (!estimate?.lines || w.lineIndex == null) return null;
  const line = estimate.lines[w.lineIndex];
  if (!line || !line.isMulledUnit) return null;

  const lineQty = parseInt(line.qty) || 1;
  const label   = line.description || 'Mulled Unit';

  return {
    isMulledPane:    true,
    mulledUnitLabel: label,
    mulledUnitTotal: lineQty,
    // overall dimensions if stored on the line
    overallWidth:    line.overallWidth  || null,
    overallHeight:   line.overallHeight || null,
  };
}

function groupWindowsForTrim(windows, estimate) {
  const result = [];
  const mulledGroups = {}; // key = mulledUnitLabel → { indices[], overallWidth, overallHeight, room, firstOrigIdx }

  windows.forEach((w, i) => {
    // Back-fill from estimate when fields are missing (legacy saved job orders).
    // We never write back to Firestore — this is purely runtime hydration.
    let effectiveIsMulledPane    = w.isMulledPane;
    let effectiveMulledUnitLabel = w.mulledUnitLabel;
    let effectiveMulledUnitTotal = w.mulledUnitTotal;
    let effectiveOverallWidth    = w.overallWidth || w.mulledOverallWidth || null;
    let effectiveOverallHeight   = w.overallHeight || w.mulledOverallHeight || null;

    if ((!effectiveMulledUnitLabel || !effectiveMulledUnitTotal) && estimate) {
      const meta = getMulledMetadataFromEstimate(w, estimate);
      if (meta) {
        effectiveIsMulledPane    = true;
        effectiveMulledUnitLabel = effectiveMulledUnitLabel || meta.mulledUnitLabel;
        effectiveMulledUnitTotal = effectiveMulledUnitTotal || meta.mulledUnitTotal;
        effectiveOverallWidth    = effectiveOverallWidth  || meta.overallWidth;
        effectiveOverallHeight   = effectiveOverallHeight || meta.overallHeight;
      }
    }

    if (effectiveIsMulledPane && effectiveMulledUnitLabel) {
      // Group key uses ONLY the unit label (without the "Unit N — " prefix
      // that CreateJobOrderPage adds when qty > 1). All copies of the same
      // mulled design collapse into one trim entry. The qty comes from
      // mulledUnitTotal (parent line.qty stamped on each pane).
      const key = effectiveMulledUnitLabel;
      if (!mulledGroups[key]) {
        mulledGroups[key] = {
          panes: [],
          room: w.room || '',
          firstOrigIdx: i,
          qty: parseInt(effectiveMulledUnitTotal) || 1,
          overallW: effectiveOverallWidth,
          overallH: effectiveOverallHeight,
          unitIndicesSeen: new Set(),
        };
      }
      // Only collect panes from ONE unit (so qty 2 of a 2-pane mulled doesn't
      // produce 4 panes). When mulledUnitIndex exists, dedupe by it.
      // When it's missing (legacy data), parse it from the description like:
      //   "WH DH / DH — 55.5"×45" — Unit 2 — R1C1"  →  unitIdx = "2"
      // If still no unit prefix exists, the line.qty was 1, so all panes belong
      // to the single unit — collect them all (they're each a unique pane).
      let unitIdx = w.mulledUnitIndex || null;
      if (unitIdx == null && typeof w.description === 'string') {
        const m = w.description.match(/Unit\s+(\d+)/i);
        if (m) unitIdx = m[1];
      }

      if (unitIdx != null) {
        // We have a unit index — only keep panes from the FIRST unit we see.
        if (mulledGroups[key].unitIndicesSeen.size === 0) {
          mulledGroups[key].unitIndicesSeen.add(unitIdx);
        }
        if (mulledGroups[key].unitIndicesSeen.has(unitIdx)) {
          mulledGroups[key].panes.push(w);
        }
      } else {
        // No unit index anywhere — line.qty was 1, all panes are this one unit.
        // Just collect each unique pane label once (e.g. R1C1, R1C2, R1C3).
        const paneKey = w.mulledPaneLabel
          || (w.description && w.description.match(/R\d+C\d+/)?.[0])
          || ('pane-' + mulledGroups[key].panes.length);
        if (!mulledGroups[key].unitIndicesSeen.has(paneKey)) {
          mulledGroups[key].unitIndicesSeen.add(paneKey);
          mulledGroups[key].panes.push(w);
        }
      }
    } else {
      // Singles carry their own qty (e.g. "2 of these 27x45 DH")
      const qty = parseInt(w.qty) || 1;
      result.push({ ...w, _trimIdx: i, _isMulledGroup: false, _qty: qty });
    }
  });

  // For each mulled group, calculate overall width and height
  Object.entries(mulledGroups).forEach(([label, group]) => {
    const panes = group.panes;

    // Overall width = sum of all pane widths in a row (panes share the same height)
    // Overall height = sum of row heights if stacked, otherwise just the shared height
    // We use the stored overallWidth/overallHeight from the line data if available.
    // If not available, we calculate: width = sum of all unique pane widths, height = max height
    let overallW = null;
    let overallH = null;

    // Try group-level overall first (back-filled from estimate), then panes
    if (group.overallW) overallW = group.overallW;
    if (group.overallH) overallH = group.overallH;
    if (!overallW || !overallH) {
      const firstPaneWithOverall = panes.find(p => p.overallWidth || p.mulledOverallWidth);
      if (firstPaneWithOverall) {
        overallW = overallW || firstPaneWithOverall.overallWidth || firstPaneWithOverall.mulledOverallWidth;
        overallH = overallH || firstPaneWithOverall.overallHeight || firstPaneWithOverall.mulledOverallHeight;
      }
    }

    // Fallback: sum widths of panes (assumes 1 row) and use max height
    if (!overallW || !overallH) {
      const totalW = panes.reduce((s, p) => s + (parseFloat(p.width) || 0), 0);
      const maxH   = Math.max(...panes.map(p => parseFloat(p.height) || 0));
      overallW = overallW || (totalW > 0 ? totalW.toFixed(1) : null);
      overallH = overallH || (maxH   > 0 ? maxH.toFixed(1)   : null);
    }

    // Build a description that shows the pane types
    const paneTypes = panes.map(p => {
      const t = TWP_TYPE_LABELS[p.twpType] || p.style || p.description || 'Window';
      // Short labels
      const short = { 'Double Hung': 'DH', 'Picture Window': 'PW', 'Casement': 'CSMT',
        'Horizontal Slider': 'HS', 'Basement Hopper': 'BSMT', '3-Lite Slider': '3HS' };
      return short[t] || t;
    });
    const panesDesc = paneTypes.join(' / ');

    result.push({
      _trimIdx:       group.firstOrigIdx,
      _isMulledGroup: true,
      isMulledUnit:   true,
      mulledUnitLabel: label,
      description:    label,
      style:          label,
      width:          overallW,
      height:         overallH,
      room:           group.room,
      lineType:       'twp_window',
      twpType:        null,
      _panesDesc:     panesDesc,
      _paneCount:     panes.length,
      _qty:           group.qty || 1,
      // Carry color/config from first pane for display
      colorOutside:   panes[0]?.colorOutside || '',
      colorInside:    panes[0]?.colorInside  || '',
    });
  });

  // Sort by original index so the order matches the job order
  result.sort((a, b) => (a._trimIdx || 0) - (b._trimIdx || 0));
  return result;
}

function drawHeader(doc, contractNumber, customer, logo, pageNum, totalPages) {
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, PAGE_W, HDR_H, 'F');
  doc.setFillColor(45, 55, 72);
  doc.rect(0, 0, 3, HDR_H, 'F');
  doc.setDrawColor(200, 160, 110);
  doc.setLineWidth(0.6);
  doc.line(0, HDR_H, PAGE_W, HDR_H);

  if (logo) { try { doc.addImage(logo, 'PNG', MARGIN + 4, 5, 16, 16); } catch(e){} }

  doc.setTextColor(45, 55, 72);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('SD Millwork', MARGIN + 24, 12);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 60, 20);
  doc.text('1300 E. 4th Ave  \u2022  Milbank, South Dakota 57252', MARGIN + 24, 17);
  doc.text('(605) 432-1834  \u2022  Toll Free (877) 224-0745  \u2022  Fax (866) 432-1718', MARGIN + 24, 21.5);

  doc.setFontSize(17);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(45, 55, 72);
  doc.text('TRIM ORDER', PAGE_W - MARGIN, 13, { align: 'right' });
  doc.setFontSize(10);
  doc.setTextColor(139, 69, 19);
  doc.text('#' + contractNumber, PAGE_W - MARGIN, 21, { align: 'right' });

  const c = customer || {};
  const fields = [
    ['Name',    c.name    || ''],
    ['Address', c.address || ''],
    ['City',    c.city    || ''],
    ['State',   c.state   || ''],
    ['Zip',     c.zip     || ''],
    ['Date',    fmtDate()],
    ['Page',    pageNum + ' of ' + totalPages],
  ];
  let fx = MARGIN + 4;
  fields.forEach(f => {
    doc.setFontSize(5.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 60, 20);
    doc.text(f[0] + ':', fx, 28);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(40, 40, 40);
    doc.setFontSize(6.5);
    doc.text(String(f[1]), fx, 33);
    fx += f[0] === 'Address' ? 36 : 26;
  });
}

function drawFooter(doc, contractNumber, customer, itemCount, pageNum, totalPages) {
  const y = PAGE_H - FTR_H;
  doc.setFillColor(255, 255, 255);
  doc.rect(0, y, PAGE_W, FTR_H, 'F');
  doc.setFillColor(45, 55, 72);
  doc.rect(0, y, 3, FTR_H, 'F');
  doc.setDrawColor(200, 160, 110);
  doc.setLineWidth(0.6);
  doc.line(0, y, PAGE_W, y);
  const now = new Date();
  const printed = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ' ' + now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  doc.setTextColor(100, 60, 20);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.text(
    'Trim Order #' + contractNumber + '  \u2022  ' + (customer?.name || '') + '  \u2022  ' + itemCount + ' item' + (itemCount !== 1 ? 's' : '') + '  \u2022  Printed: ' + printed + '  \u2022  Page ' + pageNum + ' of ' + totalPages,
    PAGE_W / 2, y + 6.5, { align: 'center' }
  );
}

export async function generateTrimOrderPDF(customerName, address, contractNumber, items, customer, returnBlob = false) {
  const logo = await getLogo();
  const doc  = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });

  drawHeader(doc, contractNumber, customer, logo, 1, 1);
  drawFooter(doc, contractNumber, customer, items.length, 1, 1);

  const head = [['Item #', 'Window / Door Type', 'Size', 'Qty', 'Room / Location', 'Trim Specifications & Notes']];
  const body = items.map(item => [
    { content: item.lineNum || '', styles: { fontStyle: 'bold', textColor: NAVY_RGB, halign: 'center' } },
    item.typeLabel || '',
    item.dimensions || '',
    { content: String(item.qty || 1), styles: { fontStyle: 'bold', textColor: NAVY_RGB, halign: 'center' } },
    item.room || '',
    item.trimNotes || '',
  ]);

  autoTable(doc, {
    startY: HDR_H + 4,
    head,
    body,
    styles: {
      fontSize: 8, cellPadding: { top: 3.5, bottom: 3.5, left: 2.5, right: 2.5 },
      textColor: DARK, lineColor: LIGHT, lineWidth: 0.15,
      overflow: 'linebreak', valign: 'middle',
    },
    headStyles: {
      fillColor: HDR_BG, textColor: NAVY_RGB, fontStyle: 'bold',
      fontSize: 7, halign: 'center', valign: 'middle', minCellHeight: 8,
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: 22, halign: 'center' },                              // Item #
      1: { cellWidth: 50, halign: 'left',  fontStyle: 'bold' },            // Type
      2: { cellWidth: 28, halign: 'center' },                              // Size
      3: { cellWidth: 14, halign: 'center', fontStyle: 'bold' },           // Qty
      4: { cellWidth: 26, halign: 'center' },                              // Room
      5: { cellWidth: 'auto', halign: 'left', minCellHeight: 14 },         // Notes
    },
    margin: { left: MARGIN, right: MARGIN, bottom: FTR_H + 8 },
    didDrawCell: (data) => {
      if (data.section === 'body' && data.column.index === 5) {
        const cellX    = data.cell.x + 2;
        const cellEndX = data.cell.x + data.cell.width - 3;
        const textH    = data.cell.textPos ? data.cell.textPos.y + 3 : data.cell.y + 7;
        doc.setDrawColor(200, 210, 220);
        doc.setLineWidth(0.15);
        let lineY = textH + 1.5;
        for (let i = 0; i < 2; i++) {
          if (lineY < data.cell.y + data.cell.height - 1) doc.line(cellX, lineY, cellEndX, lineY);
          lineY += 4;
        }
      }
    },
    didDrawPage: (data) => {
      if (data.pageNumber > 1) {
        drawHeader(doc, contractNumber, customer, logo, data.pageNumber, 1);
        drawFooter(doc, contractNumber, customer, items.length, data.pageNumber, 1);
      }
    },
  });

  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    drawHeader(doc, contractNumber, customer, logo, p, totalPages);
    drawFooter(doc, contractNumber, customer, items.length, p, totalPages);
  }

  if (returnBlob) return doc.output('blob');
  doc.save('TrimOrder_' + (contractNumber || 'DRAFT') + '_' + (customerName || 'Customer').replace(/\s+/g, '_') + '.pdf');
}

/* ══════════════════════════════════════════════════════════════════════
   MODAL COMPONENT
   ══════════════════════════════════════════════════════════════════════ */

export default function TrimOrderModal({ jobOrder, estimate, onClose, onPreview }) {
  const allLines = jobOrder?.windows || estimate?.lines?.filter(l => l.type !== 'note') || [];

  // First filter to windows/doors only, then group mulled panes into single units.
  // Pass `estimate` so we can back-fill missing mulled metadata at runtime
  // for legacy job orders saved before the metadata fix.
  const rawWindows   = allLines.filter(isWindowOrDoor);
  const trimWindows  = groupWindowsForTrim(rawWindows, estimate);


  const customer       = jobOrder?.customer || estimate?.customer || {};
  const contractNumber = jobOrder?.contractNumber || estimate?.contract?.contractNumber || '';
  const joId           = jobOrder?.id || null;
  const saved          = jobOrder?.trimOrder || null;

  // Init selection — all selected by default
  const initSelected = new Set();
  if (saved?.selected && Array.isArray(saved.selected)) {
    saved.selected.forEach(origIdx => {
      const fi = trimWindows.findIndex(w => w._trimIdx === origIdx);
      if (fi >= 0) initSelected.add(fi);
    });
  } else {
    trimWindows.forEach((_, i) => initSelected.add(i));
  }

  const initNotes = trimWindows.map((w, i) => {
    if (saved?.notes?.[w._trimIdx] !== undefined) return saved.notes[w._trimIdx];
    return '';
  });

  const [selected, setSelected]   = useState(initSelected);
  const [trimNotes, setTrimNotes] = useState(initNotes);
  const [generating, setGenerating] = useState(false);

  async function saveTrimData(sel, notes) {
    if (!joId) return;
    try {
      const origSelected = Array.from(sel).map(fi => trimWindows[fi]?._trimIdx).filter(x => x !== undefined);
      const origNotes = {};
      notes.forEach((note, fi) => { if (note && trimWindows[fi]) origNotes[trimWindows[fi]._trimIdx] = note; });
      await updateJobOrder(joId, { trimOrder: { selected: origSelected, notes: origNotes, lastUpdated: new Date().toISOString() } });
    } catch(e) { console.warn('Could not save trim data:', e); }
  }

  function toggleSelect(idx) {
    setSelected(prev => { const n = new Set(prev); n.has(idx) ? n.delete(idx) : n.add(idx); return n; });
  }
  function selectAll()  { setSelected(new Set(trimWindows.map((_, i) => i))); }
  function selectNone() { setSelected(new Set()); }
  function updateNote(idx, value) {
    setTrimNotes(prev => { const n = [...prev]; n[idx] = value; return n; });
  }

  function getLineNumber(trimIdx) {
    const w      = trimWindows[trimIdx];
    const origIdx = w?._trimIdx ?? trimIdx;
    const num    = origIdx + 1;
    const suffix = w ? getLineSuffix(w) : '';
    return contractNumber + '-' + num + suffix;
  }

  function buildItems() {
    return trimWindows
      .map((w, i) => ({ ...w, _fi: i }))
      .filter((_, i) => selected.has(i))
      .map(w => {
        // For mulled units, show the combined description and overall dimensions
        let typeLabel  = getTypeLabel(w);
        let dimensions = getDimensions(w);

        if (w._isMulledGroup) {
          // Append pane breakdown as a subtitle in the type column
          typeLabel = typeLabel + '\n(' + w._paneCount + ' panes: ' + w._panesDesc + ')';
        }

        return {
          lineNum:    getLineNumber(w._fi),
          typeLabel,
          dimensions,
          qty:        w._qty || 1,
          room:       w.room || '',
          trimNotes:  trimNotes[w._fi] || '',
        };
      });
  }

  async function handleGenerate() {
    const items = buildItems();
    if (items.length === 0) { alert('Please select at least one window/door.'); return; }
    setGenerating(true);
    await saveTrimData(selected, trimNotes);
    try {
      await generateTrimOrderPDF(customer.name || '', customer.address || '', contractNumber, items, customer, false);
    } catch(e) { console.error(e); alert('Error generating trim order.'); }
    setGenerating(false);
  }

  async function handlePreview() {
    const items = buildItems();
    if (items.length === 0) { alert('Please select at least one window/door.'); return; }
    setGenerating(true);
    await saveTrimData(selected, trimNotes);
    try {
      const blob = await generateTrimOrderPDF(customer.name || '', customer.address || '', contractNumber, items, customer, true);
      if (onPreview) {
        onPreview(blob, 'TrimOrder_' + (contractNumber || 'DRAFT') + '_' + (customer.name || 'Customer').replace(/\s+/g, '_') + '.pdf');
      } else {
        window.open(URL.createObjectURL(blob), '_blank');
      }
    } catch(e) { console.error(e); }
    setGenerating(false);
  }

  const selectedCount = selected.size;

  return (
    <div style={{position:'fixed',inset:0,zIndex:9999,background:'rgba(0,0,0,0.5)',backdropFilter:'blur(4px)',display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
      <div style={{background:'white',borderRadius:'16px',width:'100%',maxWidth:'720px',maxHeight:'90vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.3)',overflow:'hidden',fontFamily:"'Segoe UI',system-ui,sans-serif"}}>

        {/* Header */}
        <div style={{padding:'20px 24px 16px',borderBottom:'1px solid #e8e8e8',background:LIGHT_BG,flexShrink:0}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <div>
              <div style={{fontSize:'8px',fontWeight:'800',color:ACCENT,letterSpacing:'1.5px',textTransform:'uppercase',marginBottom:'4px'}}>SD MILLWORK</div>
              <div style={{fontSize:'20px',fontWeight:'800',color:NAVY}}>Trim Order</div>
              <div style={{fontSize:'12px',color:SLATE,marginTop:'2px'}}>{customer.name||'Customer'}{contractNumber?' \u2022 #'+contractNumber:''}</div>
            </div>
            <button onClick={onClose} style={{background:'none',border:'none',fontSize:'24px',color:'#aaa',cursor:'pointer',padding:'4px 8px',lineHeight:1}}>&times;</button>
          </div>
          <div style={{display:'flex',gap:'8px',marginTop:'14px'}}>
            <button onClick={selectAll}  style={{padding:'5px 12px',borderRadius:'6px',border:'1px solid '+ACCENT,background:selectedCount===trimWindows.length?ACCENT:'white',color:selectedCount===trimWindows.length?'white':ACCENT,fontSize:'11px',fontWeight:'700',cursor:'pointer',fontFamily:'inherit'}}>Select All ({trimWindows.length})</button>
            <button onClick={selectNone} style={{padding:'5px 12px',borderRadius:'6px',border:'1px solid #ccc',background:'white',color:'#888',fontSize:'11px',fontWeight:'600',cursor:'pointer',fontFamily:'inherit'}}>Clear</button>
            <div style={{marginLeft:'auto',fontSize:'12px',color:SLATE,fontWeight:'600',alignSelf:'center'}}>{selectedCount} of {trimWindows.length} selected</div>
          </div>
        </div>

        {/* Window list */}
        <div style={{flex:1,overflowY:'auto',padding:'0'}}>
          {trimWindows.length === 0 ? (
            <div style={{padding:'40px',textAlign:'center',color:'#aaa'}}>No windows or doors found.</div>
          ) : (
            trimWindows.map((w, idx) => {
              const isSelected = selected.has(idx);
              const lineNum    = getLineNumber(idx);
              const dims       = getDimensions(w);
              return (
                <div key={idx} style={{display:'flex',gap:'12px',padding:'14px 24px',borderBottom:'1px solid #f0f0f0',background:isSelected?'#f8faff':'white',opacity:isSelected?1:0.5,transition:'all 0.15s'}}>
                  <div style={{flexShrink:0,paddingTop:'2px'}}>
                    <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(idx)} style={{width:'18px',height:'18px',cursor:'pointer',accentColor:NAVY}} />
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:'flex',alignItems:'baseline',gap:'8px',flexWrap:'wrap'}}>
                      <div style={{fontSize:'11px',fontWeight:'800',color:ACCENT,fontFamily:'monospace'}}>{lineNum}</div>
                      <div style={{fontSize:'14px',fontWeight:'700',color:NAVY}}>{getTypeLabel(w)}</div>
                      {/* Mulled unit badge */}
                      {w._isMulledGroup && (
                        <div style={{fontSize:'10px',fontWeight:'700',color:'#7c3aed',background:'rgba(124,58,237,0.08)',border:'1px solid rgba(124,58,237,0.2)',padding:'2px 7px',borderRadius:'12px'}}>
                          ⊞ Joined Unit · {w._paneCount} panes · {w._panesDesc}
                        </div>
                      )}
                      {dims && <div style={{fontSize:'12px',color:SLATE,fontWeight:'600'}}>{dims}</div>}
                      <div style={{fontSize:'11px',color:NAVY,fontWeight:'700',background:'#e8f0fe',border:'1px solid #b3cdf3',padding:'2px 8px',borderRadius:'10px'}}>Qty: {w._qty || 1}</div>
                      {w.room && <div style={{fontSize:'11px',color:'#999'}}>📍 {w.room}</div>}
                    </div>
                    {/* Show overall dimensions note for mulled units */}
                    {w._isMulledGroup && dims && (
                      <div style={{fontSize:'10px',color:'#7c3aed',marginTop:'3px',fontStyle:'italic'}}>
                        Overall joined size: {dims} — use this for trim calculation
                      </div>
                    )}
                    {isSelected && (
                      <textarea
                        placeholder="Trim specs — e.g. 3¼&quot; colonial casing, coved, baseboard finish..."
                        value={trimNotes[idx] || ''}
                        onChange={e => updateNote(idx, e.target.value)}
                        rows={2}
                        style={{width:'100%',marginTop:'8px',padding:'8px 10px',borderRadius:'8px',border:'1px solid #e0e0e0',fontSize:'12px',fontFamily:'inherit',resize:'vertical',background:'#fafbfc',color:'#333',boxSizing:'border-box'}}
                      />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div style={{padding:'16px 24px',borderTop:'1px solid #e8e8e8',display:'flex',alignItems:'center',justifyContent:'space-between',background:'#fafbfc',flexShrink:0}}>
          <div style={{fontSize:'11px',color:'#999'}}>Landscape PDF &bull; No pricing &bull; Internal use</div>
          <div style={{display:'flex',gap:'8px'}}>
            <button onClick={onClose} style={{padding:'10px 18px',borderRadius:'10px',border:'1px solid #ddd',background:'white',color:'#666',fontSize:'13px',cursor:'pointer',fontFamily:'inherit'}}>Cancel</button>
            <button onClick={handlePreview} disabled={generating||selectedCount===0} style={{padding:'10px 18px',borderRadius:'10px',border:'1px solid '+ACCENT,background:'white',color:ACCENT,fontSize:'13px',fontWeight:'600',cursor:(generating||selectedCount===0)?'not-allowed':'pointer',fontFamily:'inherit',opacity:selectedCount===0?0.4:1}}>Preview</button>
            <button onClick={handleGenerate} disabled={generating||selectedCount===0} style={{padding:'10px 22px',borderRadius:'10px',border:'none',background:selectedCount===0?'#ccc':NAVY,color:'white',fontSize:'13px',fontWeight:'700',cursor:(generating||selectedCount===0)?'not-allowed':'pointer',fontFamily:'inherit'}}>
              {generating ? 'Generating…' : `⬇ Download (${selectedCount})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}